import { createContext, useContext, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { AssignToProjectModal } from '../../verticals/uploads/components/AssignToProjectModal'
import type { UploadSessionSummary } from '../../verticals/uploads/types'
import { defaultUploadsService } from '../../verticals/uploads/useUploads'
import type { UploadsService } from '../../verticals/uploads/useUploads'
import { NewWorkspaceModal } from '../../verticals/workspaces/components/NewWorkspaceModal'
import type { WorkspaceSummary } from '../../verticals/workspaces/types'
import { defaultWorkspacesService } from '../../verticals/workspaces/useWorkspaces'
import type { WorkspacesService } from '../../verticals/workspaces/useWorkspaces'
import { useToast } from './ToastProvider'

interface AssignToProjectContextValue {
  /** Opens "Add these files to a project" for a just-created upload session.
   * Fire-and-forget from the caller's perspective (UID-509) — unlike the
   * pre-UID-509 `handleSessionCreated`, this no longer gates navigation: the
   * modal now lives here, above `<Routes>`, so it survives the caller
   * navigating away immediately after upload. */
  readonly promptAssignToProject: (session: UploadSessionSummary) => void
}

const AssignToProjectContext = createContext<AssignToProjectContextValue | undefined>(undefined)

/**
 * App-wide host for "Add these files to a project" (UID-425, Bug 1),
 * relocated above `<Routes>` (UID-509) so it can stay open across the
 * navigation to the staging page — matching the design's own sequencing
 * (`Fictiv Platform.dc.html:6140`: `assignModal` is set in the SAME state
 * update as `this.go('staging')` + `runAnalysis()`, not before it).
 *
 * Modeled directly on `ToastProvider`: owns its own state, exposes one
 * imperative trigger via `useAssignToProject()`, and renders its floating UI
 * (here, two modals) as a JSX sibling of `{children}`. No `createPortal` is
 * needed for that floating UI — `Modal` already portals to `document.body`
 * itself (`Modal.tsx`), so it renders correctly regardless of how deep in
 * the tree this provider sits.
 *
 * `service`/`workspacesService` are provider-level DI props, same seam
 * `HomePage`/`UploadsPage` already exposed for testing — moved here because
 * the state/handlers that depend on them moved here too.
 *
 * The project list is fetched LAZILY, only when `promptAssignToProject`
 * fires — NOT eagerly via `useWorkspaceList` on mount. This provider now
 * lives above `<Routes>`/`RequireAuth` (UID-509), so an eager fetch on
 * mount would race the app's initial `GET /auth/session` probe and, having
 * no auth-aware retry, could come back unauthenticated and stay wrong
 * forever. By the time `promptAssignToProject` fires, an upload has already
 * happened, which itself requires an authenticated, `bom:upload`-capable
 * session — so there is no race left to have.
 */
export function AssignToProjectProvider({
  children,
  service = defaultUploadsService,
  workspacesService = defaultWorkspacesService,
}: {
  readonly children: ReactNode
  readonly service?: UploadsService
  readonly workspacesService?: WorkspacesService
}) {
  const { showToast } = useToast()
  const [projects, setProjects] = useState<readonly WorkspaceSummary[]>([])

  const [assignSession, setAssignSession] = useState<UploadSessionSummary | null>(null)
  const [assignBusy, setAssignBusy] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  // Mirrors `assignSession !== null` synchronously — `useState` reads can
  // lag behind a same-tick second call (React batches the update), so the
  // guard below needs a ref, not the state value itself.
  const assignPendingRef = useRef(false)

  function promptAssignToProject(session: UploadSessionSummary) {
    // Guards against a double-fired file input (e.g. a `<label>` both
    // nesting AND `htmlFor`-pointing at its `<input>`, which some browsers
    // resolve by firing `onChange` twice) creating two sessions from one
    // click: while a modal decision is still pending for an earlier
    // session, ignore the second call entirely instead of silently
    // overwriting `assignSession` and orphaning the first session's assign
    // flow — same guard `HomePage.tsx`'s pre-UID-509 `handleSessionCreated`
    // had via `assignResolveRef`, carried over here as a ref check that
    // replaces it.
    if (assignPendingRef.current) return
    assignPendingRef.current = true
    setAssignSession(session)
    setAssignError(null)
    void workspacesService
      .listWorkspaces()
      .then(setProjects)
      .catch(() => {
        // Silent — the same failure mode `useWorkspaceList` treats as
        // `{status:'error'}`. An empty list still lets the user proceed via
        // "Keep unfiled" or "New project"; the modal doesn't have its own
        // error slot for this, and the assign flow overall is not
        // load-bearing (an upload has already durably succeeded).
      })
  }

  /** Closes the modal. Called on an explicit "Keep unfiled" AND on
   * Escape/backdrop-dismiss (`Modal`'s own dismissal affordances) — both are
   * the same "I'm not assigning a project" decision, so both take the same
   * no-PATCH path. */
  function closeAssignModal() {
    setAssignSession(null)
    setAssignBusy(false)
    setAssignError(null)
    assignPendingRef.current = false
  }

  // UID-425 — "Keep unfiled" sends NO PATCH. The session is already unfiled
  // the instant it's created (the caller's `handleFilesSelected` call never
  // passes a `workspaceId`), so an explicit `PATCH { workspaceId: null }`
  // here would be a redundant round trip asserting a fact that is already
  // true server-side.
  function handleKeepUnfiled() {
    closeAssignModal()
  }

  async function handleAssignProject(workspaceId: string) {
    if (!assignSession) return
    setAssignBusy(true)
    setAssignError(null)
    try {
      // `updateSessionWorkspace` is OPTIONAL on `UploadsService` (see that
      // interface's doc comment) so older/other test doubles don't all need
      // updating for this one call — guard rather than assume.
      if (!service.updateSessionWorkspace) {
        throw new Error('Assigning a project isn’t supported by this uploads service.')
      }
      await service.updateSessionWorkspace(assignSession.sessionId, workspaceId)
    } catch (error) {
      // Failure must not be silent, and must not block the upload the user
      // already completed: toast it, and leave the modal open (not closed)
      // so the user can retry, pick a different project, or fall back to
      // "Keep unfiled" to proceed anyway.
      setAssignBusy(false)
      const message =
        error instanceof Error ? error.message : 'We couldn’t file these files under that project.'
      setAssignError(message)
      showToast(`Your files uploaded, but weren’t filed under a project: ${message}`, 'error')
      return
    }
    setAssignBusy(false)
    closeAssignModal()
  }

  async function handleCreateProject(request: {
    readonly name: string
    readonly visibility: 'private' | 'account'
    readonly description?: string
  }) {
    let created: { readonly id: string; readonly name: string }
    try {
      created = await workspacesService.createWorkspace(request)
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Could not create the project. Try again.',
        'error',
      )
      // Rethrow so `NewWorkspaceModal` keeps itself open for a retry (its
      // own doc comment: the CALLER owns surfacing the failure).
      throw error
    }
    setNewProjectOpen(false)
    showToast(`Project “${created.name}” created`, 'success')
    await handleAssignProject(created.id)
  }

  const value: AssignToProjectContextValue = { promptAssignToProject }

  return (
    <AssignToProjectContext.Provider value={value}>
      {children}
      {/* UID-425 (Bug 1) — hidden while `newProjectOpen` so the two never
      stack: "New project" from within the assign modal hands off to
      `NewWorkspaceModal` (below), and re-shows this one only if the
      resulting auto-assign PATCH fails (see `handleAssignProject`). */}
      <AssignToProjectModal
        open={assignSession !== null && !newProjectOpen}
        fileCount={assignSession?.fileCount ?? 0}
        sessionName={assignSession?.name ?? ''}
        workspaces={projects}
        busy={assignBusy}
        error={assignError}
        onSelectWorkspace={(workspaceId) => void handleAssignProject(workspaceId)}
        onKeepUnfiled={handleKeepUnfiled}
        onCreateProject={() => setNewProjectOpen(true)}
        onClose={handleKeepUnfiled}
      />
      <NewWorkspaceModal
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        onCreate={handleCreateProject}
      />
    </AssignToProjectContext.Provider>
  )
}

export function useAssignToProject(): AssignToProjectContextValue {
  const context = useContext(AssignToProjectContext)
  if (!context) {
    throw new Error('useAssignToProject() must be used within an <AssignToProjectProvider>')
  }
  return context
}
